import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
import speciesName from '../utils/speciesName';
import debouncedSearch from '../utils/debouncedSearch';
import wantsCol from '../utils/wantsCol';
/**
 * Species search box used at the top of the homepage's right column, on the
 * species page itself, and inside SpeciesAssignModal - queries
 * champis-species' /api/species-autocomplete endpoint (GBIF-backed).
 *
 * By default, selecting a result navigates to that species' page (see
 * SpeciesPage). Pass `onselect(species)` to override that (used by
 * SpeciesAssignModal to add the pick to a list instead of navigating away).
 * Pass `highertaxon`/`highergroup` to constrain results to a taxon group
 * (e.g. 'fungi', 'plants') - see SpeciesAutocompleteService's TAXON_QUERIES.
 * Pass `hideLabel` to drop the "Chercher une espèce" label above the input -
 * useful when a caller already has its own field label right above this
 * (e.g. ExhibitionFindingEditModal's "Espèce"), where the two together just
 * repeated the same instruction twice.
 *
 * Searches ChecklistBank instead of GBIF (GET /api/col-autocomplete, never
 * both at once - see ColAutocompleteService's own docblock) when the actor
 * prefers COL (wantsCol()) AND no `onselect` was given - every `onselect`
 * usage (SpeciesAssignModal, ExhibitionFindingEditModal) needs a real
 * gbif_id to submit regardless of the actor's own display preference, the
 * same reasoning that keeps SpeciesCandidatesResource GBIF-only. Tracked in
 * `usingCol` (set by search(), read by select()) rather than re-deriving
 * wantsCol() at select time, since it must reflect whichever endpoint
 * *actually served these specific results* - not the actor's preference at
 * the moment of clicking, which can't have changed mid-dropdown but is
 * clearer to reason about this way.
 */
export default class SpeciesSearchWidget extends Component {
  query = '';
  results = [];
  loading = false;
  open = false;
  showHelp = false;
  usingCol = false;
  gbifUnavailable = false;
  debounceHandle = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.query = '';
    this.results = [];
    this.loading = false;
    this.open = false;
    this.showHelp = false;
    this.usingCol = false;
    this.debounceHandle = null;
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-species.forum.search.${key}`, params);
    };
    const labelText = this.attrs.label || app.translator.trans('champis-species.forum.search.label');

    // The <input> below needs an accessible name either way - normally the
    // visible label wrapping it provides one, but hideLabel drops that
    // (see this component's own docblock on why), so it falls back to an
    // aria-label built from the exact same text a visible label would have
    // shown, rather than leaving the field silently unlabeled for a screen
    // reader.
    const input = m("div", {
      className: "ChampisSpeciesSearch-input"
    }, m("button", {
      type: "button",
      className: "ChampisSpeciesSearch-helpToggle",
      "aria-label": extractText(t('help_toggle_accessible_label')),
      "aria-expanded": this.showHelp ? 'true' : 'false',
      title: extractText(t('help_toggle_accessible_label')),
      onclick: () => {
        this.showHelp = !this.showHelp;
      }
    }, m("i", {
      className: "icon fas fa-question-circle",
      "aria-hidden": "true"
    })), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: extractText(this.attrs.placeholder || app.translator.trans('champis-species.forum.search.placeholder')),
      "aria-label": this.attrs.hideLabel ? extractText(labelText) : undefined,
      value: this.query,
      oninput: e => this.onInput(e.target.value),
      onfocus: () => {
        this.open = this.results.length > 0;
      },
      onblur: () => {
        // Closes the list when the field loses focus to anything else: a
        // press inside the list doesn't blur it (the list's onmousedown
        // below), so a click there completes however long the button is
        // held. The delay alone used to drop slow clicks - the list was
        // gone before the mouseup (reported 2026-10-12).
        setTimeout(() => {
          this.open = false;
          m.redraw();
        }, 150);
      }
    }), this.loading && m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }));
    return m("div", {
      className: "ChampisSpeciesSearch"
    }, this.attrs.hideLabel ? input : m("label", null, m("span", {
      className: "ChampisSpeciesSearch-label"
    }, m("i", {
      className: "icon fas fa-search",
      "aria-hidden": "true"
    }), " ", labelText), input), this.showHelp && m("div", {
      className: "ChampisSpeciesSearch-help"
    }, m("h3", null, t('help_title')), m("div", {
      className: "ChampisSpeciesSearch-helpGroup"
    }, m("p", null, t('help_intro_1')), m("p", null, t('help_intro_2'))), m("div", {
      className: "ChampisSpeciesSearch-helpGroup"
    }, m("p", null, m("strong", null, t('help_example_label')), " ", t('help_example_1', {
      species: m("em", null, "Amanita muscaria"),
      query: 'ama musca'
    })), m("p", null, t('help_example_2', {
      species: m("em", null, "Amanita muscaria var. formosa"),
      query: 'ama musc for'
    }))), m("div", {
      className: "ChampisSpeciesSearch-helpGroup"
    }, m("p", null, m("strong", null, t('help_remark_label')), " ", t('help_remark')))), this.open && this.results.length > 0 && m("ul", {
      className: "ChampisSpeciesSearch-results",
      onmousedown: e => e.preventDefault()
    }, this.gbifUnavailableNotice(), this.groupedResults().map(_ref => {
      let {
        status,
        species
      } = _ref;
      return [m("li", {
        key: 'group-' + status,
        className: "ChampisSpeciesSearch-groupHeader"
      }, status), ...species.map(s => m("li", {
        key: s.id,
        onclick: () => this.select(s)
      }, s.isInChampisNet ? m("strong", null, "*") : null, " ", speciesName(s)))];
    })), this.open && !this.loading && this.results.length === 0 && m("ul", {
      className: "ChampisSpeciesSearch-results",
      onmousedown: e => e.preventDefault()
    }, this.gbifUnavailableNotice(), m("li", {
      className: "ChampisSpeciesSearch-empty"
    }, app.translator.trans('champis-species.forum.search.empty'))));
  }
  gbifUnavailableNotice() {
    return this.gbifUnavailable && m("li", {
      className: "ChampisSpeciesSearch-notice"
    }, app.translator.trans('champis-species.forum.search.gbif_unavailable'));
  }

  /**
   * Buckets results by status (e.g. "Valide" / "Synonyme") without
   * re-sorting - the API already puts accepted species before synonyms.
   */
  groupedResults() {
    const groups = [];
    this.results.forEach(species => {
      let group = groups.find(g => g.status === species.status);
      if (!group) {
        group = {
          status: species.status,
          species: []
        };
        groups.push(group);
      }
      group.species.push(species);
    });
    return groups;
  }
  onInput(value) {
    this.query = value;
    this.open = false;
    this.showHelp = false;
    debouncedSearch(this, value, query => this.search(query));
  }
  search(query) {
    this.loading = true;
    m.redraw();

    // See this class's own docblock - onselect always means an assignment/
    // correction context, which needs a real gbif_id regardless of the
    // actor's own display preference, so it's excluded here even though
    // none of those callers currently happen to also pass highertaxon.
    this.usingCol = !this.attrs.onselect && wantsCol();
    const filter = {
      query
    };
    if (this.attrs.highertaxon) filter.highertaxon = this.attrs.highertaxon;
    if (this.attrs.highergroup) filter.highergroup = this.attrs.highergroup;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + (this.usingCol ? '/col-autocomplete' : '/species-autocomplete'),
      params: {
        filter
      }
    }).then(response => {
      // Stale response for a query the user has since changed/cleared.
      if (query !== this.query.trim()) return;
      this.results = (response.data || []).map(item => ({
        id: item.id,
        ...item.attributes
      }));
      this.gbifUnavailable = !!response.meta?.gbifUnavailable;
      this.loading = false;
      this.open = true;
      m.redraw();
    }).catch(() => {
      this.loading = false;
      m.redraw();
    });
  }
  select(species) {
    this.query = species.scientificName || '';
    this.open = false;
    if (this.attrs.onselect) {
      this.attrs.onselect(species);
    } else if (this.usingCol) {
      // species.id is a ChecklistBank id here (see ColAutocompleteService's
      // own docblock), never a gbif_id - TaxonResource resolves either a
      // real champis.net species or a pure-COL taxon from the same id.
      m.route.set(app.route.taxon(species.id, species.canonicalName));
    } else {
      m.route.set(app.route.species(species.id, species.pageSlug || species.canonicalName));
    }
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesSearchWidget', SpeciesSearchWidget);